import { Block } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/CheckOutlined'
import WarningIcon from '@mui/icons-material/Warning'
import { Tooltip } from '@mui/material'
import type { TFunction } from 'i18next'
import type { DateTime } from 'luxon'
import type { ReactElement } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { ActivationGroup, ActivationGroupUuid } from '@/features/activationGroup/types'
import type { BidType, MarketProgram } from '@/features/bidding/constants'
import { usePtuExpiration } from '@/features/bidding/hooks/usePtuExpiration'
import type { ExtendedBid, OfferedAndForecastAndSuggestedPtu } from '@/features/bidding/types/bid'
import { capacityPtuFor } from '@/features/bidding/utils/capacityPtuFor'

import type { UseBidOfferCapacityBidsResult } from './useBidOfferCapacityBids'

export type UseBidOfferTabMetadataResult = {
  tabIcons: Map<ActivationGroupUuid, ReactElement>
}

/**
 * Hook to calculate tab icons for bid offer tabs based on validation rules.
 * Returns icons indicating bid status: valid (green check), outside limits (red check),
 * zero volume (block), or underbidding (warning).
 *
 * @param eligibleActivationGroups - Array of activation groups to calculate icons for
 * @param currentPtusByActivationGroup - Map of current PTU values by activation group UUID
 * @param capacityBids - Capacity bids utility functions from useBidOfferCapacityBids
 * @returns An object containing a Map of tab icons by activationGroupUuid
 *
 * Icon priority (highest to lowest):
 * 1. Warning (orange) - Underbidding capacity bid
 * 2. Block (gray) - All volumes are zero
 * 3. CheckIcon (red) - Volume outside bidding limits
 * 4. CheckIcon (green) - Valid bid
 *
 * @example
 * ```tsx
 * const { tabIcons } = useBidOfferTabMetadata(eligibleActivationGroups, currentPtusByActivationGroup, capacityBids)
 *
 * const icon = tabIcons.get(activationGroupUuid)
 * ```
 */
export function useBidOfferTabMetadata(
  eligibleActivationGroups: ActivationGroup[],
  currentPtusByActivationGroup: Map<string, OfferedAndForecastAndSuggestedPtu[]>,
  capacityBids: UseBidOfferCapacityBidsResult,
): UseBidOfferTabMetadataResult {
  const { t } = useTranslation()
  const { isPtuEditable } = usePtuExpiration()

  const tabIcons = useMemo(() => {
    return calculateTabIcons(
      eligibleActivationGroups,
      currentPtusByActivationGroup,
      capacityBids.getAcceptedCapacityBidOnThisDay,
      isPtuEditable,
      t,
    )
  }, [eligibleActivationGroups, currentPtusByActivationGroup, capacityBids, isPtuEditable, t])

  // Memoize the return object to prevent unnecessary recalculations in dependent hooks
  return useMemo(
    () => ({
      tabIcons,
    }),
    [tabIcons],
  )
}

function calculateTabIcons(
  activationGroups: ActivationGroup[],
  currentPtusByActivationGroup: Map<string, OfferedAndForecastAndSuggestedPtu[]>,
  getAcceptedCapacityBidOnThisDay: (activationGroupUuid: string) => ExtendedBid | undefined,
  isPtuEditable: (ptuStart: DateTime, bidType: BidType, marketProgram?: MarketProgram) => boolean,
  t: TFunction,
): Map<ActivationGroupUuid, ReactElement> {
  const tabIcons = new Map<ActivationGroupUuid, ReactElement>()

  for (const ag of activationGroups) {
    const currentPtus = currentPtusByActivationGroup.get(ag.uuid)
    if (!currentPtus) continue

    const tabIcon = getTabCompleteIcon(currentPtus, ag, getAcceptedCapacityBidOnThisDay(ag.uuid), isPtuEditable, t)
    if (tabIcon) {
      tabIcons.set(ag.uuid, tabIcon)
    }
  }

  return tabIcons
}

function getTabCompleteIcon(
  currentPtus: OfferedAndForecastAndSuggestedPtu[],
  activationGroup: ActivationGroup,
  acceptedCapacityBid: ExtendedBid | undefined,
  isPtuEditable: (ptuStart: DateTime, bidType: BidType, marketProgram?: MarketProgram) => boolean,
  t: TFunction,
): ReactElement | undefined {
  if (currentPtus.length === 0) return undefined

  // Underbid icon takes precedence over the "bidding limits" icon and the "no bid" icon
  if (acceptedCapacityBid && isUnderbidding(currentPtus, acceptedCapacityBid, isPtuEditable))
    return (
      <Tooltip title={t('bidding.create_bid.tabs.underbid_tooltip')}>
        <WarningIcon sx={{ color: 'orange' }} />
      </Tooltip>
    )

  if (currentPtus.every((ptu) => ptu.offeredVolume.quantity === 0)) return <Block sx={{ color: 'gray' }} />

  if (currentPtus.some((ptu) => hasPtuViolatingBiddableVolumeRange(ptu, activationGroup)))
    return (
      <Tooltip title={t('bidding.create_bid.tabs.outside_bidding_limits_tooltip')}>
        <CheckIcon sx={{ color: 'red' }} />
      </Tooltip>
    )

  return <CheckIcon sx={{ color: 'green' }} />
}

function hasPtuViolatingBiddableVolumeRange(
  ptu: OfferedAndForecastAndSuggestedPtu,
  activationGroup: ActivationGroup,
): boolean {
  return (
    ptu.offeredVolume.quantity !== 0 &&
    (ptu.offeredVolume.quantity < (activationGroup.biddableVolumeRange?.min ?? 0) ||
      ptu.offeredVolume.quantity > (activationGroup.biddableVolumeRange?.max ?? 0))
  )
}

function isUnderbidding(
  currentPtus: OfferedAndForecastAndSuggestedPtu[],
  acceptedCapacityBid: ExtendedBid,
  isPtuEditable: (ptuStart: DateTime, bidType: BidType, marketProgram?: MarketProgram) => boolean,
): boolean {
  if (!acceptedCapacityBid || acceptedCapacityBid.ptus.length === 0) return false

  return !!currentPtus.find((currentPtu) => {
    const isEditable = isPtuEditable(
      currentPtu.ptu.start,
      acceptedCapacityBid.bidType,
      acceptedCapacityBid.marketProgram,
    )
    const capacityPtu = capacityPtuFor(acceptedCapacityBid, currentPtu.ptu)
    return isEditable && (capacityPtu?.acceptedVolume?.quantity ?? 0) > (currentPtu.offeredVolume.quantity ?? 0)
  })
}
